import { DatePipe } from '@angular/common';
import { HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, Subject, catchError } from 'rxjs';
import { ServiceBase } from './servicebase.service';
import { TableLazyLoadEvent } from 'primeng/table';
import { TableLazyLoadEventExt } from '../shared/models/primeng-ext';

@Injectable({
  providedIn: 'root'
})
export class CrimeGunService extends ServiceBase {
  private _crimeGunTriageData$ = new Subject<ServerModels.CrimeGunTriageViewModel[]>();
  crimeGunTriageData$ = this._crimeGunTriageData$.asObservable();
  loadingCrimeGunList: boolean = false;

  private _nibinCaseData: Subject<ServerModels.ENESS_NIBINCasesDTO> = new Subject<ServerModels.ENESS_NIBINCasesDTO>();
  nibinCaseData$: Observable<ServerModels.ENESS_NIBINCasesDTO> = this._nibinCaseData.asObservable();

  public addCrimeGunTriage(cgTriage: ServerModels.ENESS_CrimeGunTriage): Observable<{ cgicHistory: ServerModels.CrimeGunTriageViewModel[], agencyHistory: ServerModels.CrimeGunTriageViewModel[] }> {
    return this.http.post<{ cgicHistory: ServerModels.CrimeGunTriageViewModel[], agencyHistory: ServerModels.CrimeGunTriageViewModel[] }>(`${this.apiRoot}/api/CrimeGun/AddCrimeGunTriage`, cgTriage, this.httpNoCache).pipe(catchError(this.throwError<any>('addCrimeGunTriage', 'defaultMsg', null, null)));
  }

  public addOrUpdateCustomCrimeGunFields(crimeGunData: ServerModels.GunDetailCustomFields): Observable<ServerModels.GunDetailCustomFields> {
    return this.http.post<ServerModels.GunDetailCustomFields>(`${this.apiRoot}/api/CrimeGun/AddOrUpdateCustomCrimeGunFields`, crimeGunData, this.httpOptions).pipe(catchError(this.throwError<any>('addOrUpdateCustomCrimeGunFields', 'defaultMsg', null, null)));
  }

  public downloadCrimeGunPdf(crimeGunNumberId: string, sortOrder: string, showTriageLevel: boolean, showTriageComment: boolean, triageDate: string): Observable<Blob> {

    let options = {
      responseType: this.httpBlob.responseType,
      headers: this.httpBlob.headers,
      params: {
        'crimeGunNumberId': crimeGunNumberId,
        'sortOrder': sortOrder,
        'showTriageLevel': String(showTriageLevel),
        'showTriageComment': String(showTriageComment),
        'triageDate': triageDate || '',
        'datePrinted': new DatePipe('en-US').transform(new Date(), 'MM/dd/yyyy')
      }
    };
    return this.http.put<Blob>(`${this.apiRoot}/api/CrimeGun/DownloadCrimeGunPdf`, options.params, this.httpBlob).pipe(catchError(this.throwError<any>('addCrimeGunTriage', 'defaultMsg', null, null)));
  }

  public getCrimeGunCasesPageResults(lazyLoadEvent: TableLazyLoadEventExt): Observable<ServerModels.CrimeGunCasesPageResultsDTO> {
    let headersObj = this.httpOptions.headers.append('Cache-Control', 'max-age=1800');
    let options = {
      headers: headersObj
    };
    this.loadingCrimeGunList = true;
    return this.http.post<{ hits: ServerModels.HomePageView[], totalRecords: number }>(`${this.apiRoot}/api/CrimeGun/CrimeGunCasesPageResults`, JSON.stringify(lazyLoadEvent), options).pipe(catchError(this.throwError<any>('getAllGunCasesPaged', 'crimeGunCasesListMsg', 'Retrieve failed:', null)));
  }

  public getCrimeGunTriageHistory(crimeGunId: string): Observable<{ cgicHistory: ServerModels.CrimeGunTriageViewModel[], agencyHistory: ServerModels.CrimeGunTriageViewModel[] }> {
    let options = {
      headers: this.httpNoCache.headers,
      params: {
        'crimeGunNumberID': crimeGunId
      }
    }; 

    return this.http.get<{ cgicHistory: ServerModels.CrimeGunTriageViewModel[], agencyHistory: ServerModels.CrimeGunTriageViewModel[] }>(`${this.apiRoot}/api/CrimeGun/GetCrimeGunTriage`, options).pipe(catchError(this.swallowError<any>('getCrimeGunTriageHistory', 'defaultMsg', 'Failed to retreive Triage History:', null)));
  }

  public getCrimeGunFirearm(crimeGunId: string): Observable<ServerModels.CrimeGunFirearm> {
    let headersObj = this.httpOptions.headers.append('Skip-Loader', 'true');
    let options = {
      headers: headersObj,
      params: {
        'crimeGunNumberId': crimeGunId
      }
    };

    return this.http.get<ServerModels.CrimeGunFirearm>(`${this.apiRoot}/api/CrimeGun/GetCrimeGunFirearm`, options).pipe(catchError(this.swallowError<any>('getCrimeGunFirearm', 'defaultMsg', null, null)));
  }

  public getCrimeGunDetailsViewModel(crimeGunId: string): Observable<ServerModels.CrimeGunDetailsViewModel> {
    let headersObj = this.httpOptions.headers.append('Skip-Loader', 'true');
    let options = {
      headers: headersObj,
      params: {
        'crimeGunNumberId': crimeGunId
      }
    };

    return this.http.get<ServerModels.CrimeGunDetailsViewModel>(`${this.apiRoot}/api/CrimeGun/GetCrimeGunDetails`, options).pipe(catchError(this.swallowError<any>('getCrimeGunDetails', 'crimeGunDetailMsg', null, null)));
  }

  public getCrimeGunRelatedInformationReports(crimeGunId: string): Observable<ServerModels.CrimeGunIntelligenceReportViewModel[]> {
    let headersObj = this.httpOptions.headers.append('Skip-Loader', 'true');
    let options = {
      headers: headersObj,
      params: {
        'crimeGunNumberId': crimeGunId
      }
    };

    return this.http.get<ServerModels.CrimeGunIntelligenceReportViewModel[]>(`${this.apiRoot}/api/CrimeGun/GetCrimeGunRelatedIntelligenceReports`, options).pipe(catchError(this.swallowError<any>('getCrimeGunRelatedInformationReports', 'crimeGunDetailMsg', null, null)));
  }

  public getCrimeGunRelatedResults(crimeGunId: string): Observable<ServerModels.ResultTableViewModel[]> {
    let headersObj = this.httpOptions.headers.append('Skip-Loader', 'true');
    let options = {
      headers: headersObj,
      params: {
        'crimeGunNumberId': crimeGunId
      }
    };

    return this.http.get<ServerModels.ResultTableViewModel[]>(`${this.apiRoot}/api/CrimeGun/GetCrimeGunRelatedResults`, options).pipe(catchError(this.swallowError<any>('getCrimeGunRelatedResults', 'crimeGunDetailMsg', null, null)));
  }

  public getCrimeGunCustomFields(firearmId: string): Observable<ServerModels.GunDetailCustomFields> {
    let options = {
      headers: this.httpNoCache.headers,
      params: {
        'firearmId': firearmId || ''
      }
    };

    return this.http.get<ServerModels.GunDetailCustomFields>(`${this.apiRoot}/api/CrimeGun/GetCrimeGunCustomFields`, options).pipe(catchError(this.swallowError<any>('getCrimeGunCustomFields', 'defaultMsg', null, null)));
  }

  public getCrimeGunLinkedHits(crimeGunId: string): Observable<ServerModels.LinkedHitsViewModel[]> {
    let options = {
      headers: this.httpOptions.headers,
      params: {
        'crimeGunNumberId': crimeGunId
      }
    };

    return this.http.get<ServerModels.LinkedHitsViewModel[]>(`${this.apiRoot}/api/CrimeGun/GetCrimeGunLinkedHits`, options).pipe(catchError(this.swallowError<any>('getCrimeGunLinkedHits', 'defaultMsg', null, null))); 
  }

  public getStepOutListCSV(crimeGunId: string, steps: string): Observable<HttpResponse<Object>> {
    let options = {
      headers: this.httpOptions.headers,
      params: {
        'crimeGunNumberID': crimeGunId,
        'numberOfSteps': steps
      }
    };

    return this.http.get(`${this.apiRoot}/api/CrimeGun/GetStepOutListCSV`, options).pipe(catchError(this.swallowError<any>('getStepOutListCSV', 'defaultMsg', null, null))); 
  }

  public getTriageTierLevels(): Observable<ServerModels.ENESS_REF_TriageTierLevel[]> {
    return this.http.get<ServerModels.ENESS_REF_TriageTierLevel[]>(`${this.apiRoot}/api/CrimeGun/GetTriageTiers`, this.httpOptions).pipe(catchError(this.swallowError<any>('getTriageTierLevels', 'defaultMsg', null, null))); 
  }

  public notifyCrimeGunTriage(triageList: ServerModels.CrimeGunTriageViewModel[]): void {
    this._crimeGunTriageData$.next(triageList);
  }

  public getCrimeGunNewDataTypes(): Observable<ServerModels.ENESS_REF_CrimeGunNewDataTypes[]> {
    return this.http.get<ServerModels.ENESS_REF_CrimeGunNewDataTypes[]>(`${this.apiRoot}/api/CrimeGun/GetCrimeGunNewDataTypes`, this.httpOptions);
  }

  public getNIBINCaseData(nibinCaseId: string): void {
    let options = {
      headers: this.httpOptions.headers,
      params: {
        'nibinCaseId': nibinCaseId
      }
    };
    this.http.get<ServerModels.ENESS_NIBINCasesDTO>(`${this.apiRoot}/api/CrimeGun/GetNIBINCaseData`, options)
      .pipe(catchError(this.swallowError<any>('getNIBINCaseData', 'nibinCaseDataMsg', null, null)))
      .subscribe((caseData: ServerModels.ENESS_NIBINCasesDTO) => this._nibinCaseData.next(caseData));
  }

  public addOrUpdateLegacyATFGunNumber(crimeGunData: ServerModels.CrimeGunFirearm): Observable<ServerModels.CrimeGunFirearm> {
    return this.http.post<ServerModels.CrimeGunFirearm>(`${this.apiRoot}/api/CrimeGun/AddOrUpdateLegacyATFGunNumber`, crimeGunData, this.httpOptions).pipe(catchError(this.throwError<any>('addOrUpdateLegacyATFGunNumber', 'defaultMsg', null, null)));
  }

  public searchByCrimeGunNumber(crimeGunNumber: string): Observable<ServerModels.CrimeGunSelectionValues[]> {
    let options = {
      headers: this.httpOptions.headers,
      params: {
        'crimeGunNumber': crimeGunNumber
      }
    };

    return this.http.get<ServerModels.CrimeGunSelectionValues[]>(`${this.apiRoot}/api/CrimeGun/SearchByCrimeGunNumber`, options).pipe(catchError(this.swallowError<any>('searchByCrimeGunNumber', 'defaultMsg', null, null))); 
  }

  public getLinkedShootingsCount(crimeGunNumberId: string): Observable<number> {
    let options = {
      headers: this.httpOptions.headers,
      params: {
        'crimeGunNumberId': crimeGunNumberId
      }
    };

    return this.http.get<number>(`${this.apiRoot}/api/CrimeGun/GetLinkedShootingsCount`, options).pipe(catchError(this.swallowError<any>('getLinkedShootingsCount', 'defaultMsg', null, null))); 
  }

}
